import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { InformationCircleLineSIcon } from '@alfalab/icons-glyph/InformationCircleLineSIcon';
import cn from 'classnames';
import { BlockHolder } from '@alfa-bank/site-block-holder';
import { Grid } from '@alfa-bank/site-grid-universal';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { Switch } from '@alfalab/core-components-switch';
import type { FieldMetaState } from 'react-final-form';
import { useForm } from 'react-final-form';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import type { ITextV2PublicProps, TView } from '@alfa-bank/site-text-v2/src/models';

import { Carousel } from '../carousel';

import type stylesDesktop from './desktop/desktop.css';
import { getFullProductsFieldName } from './helpers';
import type { IProductOption, TProduct } from './models';

export const AdditionalProductMultiple: FC<TProduct> = ({
    Block,
    ProductCard,
    Text,
    Tooltip,
    bottomSheets,
    card,
    carousel,
    checkedByDefault = true,
    className,
    defaultProducts,
    imagePosition = 'top',
    isFlexibleAlfaCard,
    isMobile = false,
    name,
    products,
    styler,
    styles,
    title,
    titleHint,
    titleView,
    tooltip,
}) => {
    const view: ITextV2PublicProps | { view: TView } = isFlexibleAlfaCard
        ? {
              view: isMobile ? 'xsmall' : 'small',
          }
        : titleView;
    const formApi = useForm();
    const allProductsFieldName = getFullProductsFieldName(name);

    const fieldMeta = useRef<FieldMetaState<string[]>>();

    const [isChecked, setChecked] = useState(checkedByDefault);

    const initialValue = products?.[0]?.value || '';

    const mobileProps =
        isMobile && bottomSheets
            ? {
                  bottomSheetProps: {
                      hasCloser: true,
                  },
                  title: (
                      <Text styler={{ marginBottom: 0, marginTop: 24, padding: 0 }} view="small">
                          {bottomSheets.title}
                      </Text>
                  ),
                  content: (
                      <Text
                          styler={{ marginBottom: 0, marginTop: 0, padding: 0 }}
                          view="primary-small"
                      >
                          {bottomSheets.content}
                      </Text>
                  ),
                  actionButtonTitle: bottomSheets.button,
                  footerClassName: styles.bottomSheetFooter,
              }
            : null;

    const hasHint = !isMobile && titleHint;

    const hasTooltip = isMobile ? bottomSheets : tooltip;

    useEffect(() => {
        return () => {
            if (fieldMeta.current) {
                actions.updateAction(fieldMeta.current, {
                    [name]: undefined,
                });
            }

            formApi.change(allProductsFieldName, []);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Block className={className} notPadded styler={styler}>
            <Grid
                className={carousel?.carouselTitle}
                colAutoWidth
                justify="between"
                styler={{ marginBottom: 16 }}
            >
                <Block notPadded>
                    <BlockHolder
                        alignX="left"
                        alignY="bottom"
                        styler={hasHint ? { marginBottom: 8 } : {}}
                        width="auto"
                    >
                        <Text
                            className={
                                isFlexibleAlfaCard
                                    ? cn(styles.flexibleDesignTitle, styles.external)
                                    : undefined
                            }
                            tag="div"
                            {...view}
                            styler={{ marginTop: 0, marginBottom: 0 }}
                        >
                            {title}
                            {hasTooltip && (
                                <Tooltip
                                    arrowClassName={cn(styles.bundleArrow, styles.external)}
                                    content={tooltip}
                                    fallbackPlacements={['bottom', 'top']}
                                    popoverClassName={cn(styles.bundlePopover, styles.external)}
                                    position="top"
                                    preventOverflow
                                    targetClassName={
                                        isFlexibleAlfaCard && !isMobile
                                            ? cn(
                                                  (styles as typeof stylesDesktop)
                                                      .flexibleDesignTarget,
                                                  styles.external
                                              )
                                            : cn(styles.target, styles.external)
                                    }
                                    trigger="hover"
                                    view="hint"
                                    {...mobileProps}
                                >
                                    <InformationCircleLineSIcon
                                        className={cn(styles.infoIcon, styles.external)}
                                        height={20}
                                        width={20}
                                    />
                                </Tooltip>
                            )}
                        </Text>
                    </BlockHolder>
                    {hasHint && (
                        <Text
                            className={cn(styles.colorLightGrey, styles.external)}
                            kind="caption"
                            styler={{ marginTop: 0, marginBottom: 0 }}
                            view="component-secondary"
                        >
                            {titleHint}
                        </Text>
                    )}
                </Block>
                <Switch
                    block
                    checked={isChecked}
                    className={cn(styles.switch, styles.external)}
                    dataTestId="additional-product-multiple-switch"
                    onChange={(_, payload) => {
                        if (payload) {
                            setChecked(payload.checked);
                            if (!payload.checked) {
                                formApi.change(
                                    allProductsFieldName,
                                    Array.from(defaultProducts || [])
                                );
                            }
                        }
                    }}
                />
            </Grid>

            <Field<string[]>
                name={name}
                render={({ input, meta }) => {
                    fieldMeta.current = meta;

                    const defaultProductsArray = defaultProducts || [];
                    if (isChecked) {
                        const value = input.value || initialValue;
                        actions.updateAction(meta, {
                            [name]: [...(defaultProductsArray || []), value].join(','),
                        });
                        formApi.change(allProductsFieldName, [...defaultProductsArray, value]);
                    } else {
                        actions.updateAction(meta, {
                            [name]: defaultProductsArray.join(','),
                        });
                        formApi.change(allProductsFieldName, defaultProductsArray);
                    }

                    return (
                        <>
                            {isChecked && (
                                <Carousel<IProductOption>
                                    containerClassName={cn(
                                        styles.carouselContainer,
                                        styles.external,
                                        carousel?.containerClassName
                                    )}
                                    contentClassName={cn(
                                        styles.carouselContent,
                                        styles.external,
                                        carousel?.contentClassName
                                    )}
                                    gridClassName={cn(styles.carouselGrid, styles.external)}
                                    isPreselected
                                    options={products}
                                    renderCard={({ isSelected, onClick, option }) => (
                                        <ProductCard
                                            key={option.value}
                                            backgroundColor={option.backgroundColor}
                                            height={card?.height}
                                            imagePosition={imagePosition}
                                            imageSrc={option.image}
                                            isSelected={isSelected}
                                            onClick={(e, value) => {
                                                formApi.change(name, value);
                                                formApi.change(allProductsFieldName, [
                                                    ...(defaultProducts || []),
                                                    value,
                                                ]);
                                                onClick(e);
                                            }}
                                            subTitle={option.subTitle}
                                            title={option.title}
                                            value={option.value}
                                            width={card?.width}
                                        />
                                    )}
                                />
                            )}
                        </>
                    );
                }}
            />
        </Block>
    );
};
